import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";

import Menu from "../../../components/Menu";
import Loading from "../../../components/Loading";

import api from "../../../services/api";

const endpoint = "/cadastros/fio-mapeamento";

const compactHeaderStyle = {
  fontSize: 12,
  padding: "5px 7px",
  whiteSpace: "nowrap",
};

const compactBodyStyle = {
  fontSize: 12,
  padding: "4px 7px",
  whiteSpace: "nowrap",
};

const pendingInfoStyle = {
  backgroundColor: "#fff7ed",
  border: "1px solid #fed7aa",
  borderLeft: "4px solid #f97316",
  color: "#7c2d12",
  fontSize: 13,
  marginBottom: 12,
  padding: "10px 12px",
};

const pendingHeaderStyle = {
  ...compactHeaderStyle,
  backgroundColor: "#ffedd5",
  color: "#7c2d12",
};

const pendingBodyStyle = {
  ...compactBodyStyle,
  backgroundColor: "#fff7ed",
};

const activeBodyStyle = {
  ...compactBodyStyle,
  color: "#166534",
  fontWeight: "bold",
};

const initialFormData = {
  produto_id: null,
  fio_spi_id: null,
};

/*
 * Este mapeamento liga fios do portal a fios do SPI quando o relacionamento
 * original pelo spi_id não é suficiente para representar equivalências.
 */
async function fetchFiosSpi() {
  const response = await api.get(`${endpoint}/fios-spi`);

  return response.data.data;
}

async function fetchFiosPortal() {
  const response = await api.get("titulos");

  return response.data;
}

function formatStatus(row) {
  return row.ativo ? "Ativo" : "Inativo";
}

function formatProduto(row) {
  return row.produto.descricao;
}

function formatFioSpi(row) {
  return row.fio_spi.descricao;
}

export default function FioMapeamento() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [fiosSpi, setFiosSpi] = useState([]);
  const [fiosPortal, setFiosPortal] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const showError = useCallback((detail) => {
    if (!toast.current) {
      return;
    }

    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail,
      life: 5000,
    });
  }, []);

  const showSuccess = useCallback((detail) => {
    if (!toast.current) {
      return;
    }

    toast.current.show({
      severity: "success",
      summary: "Sucesso",
      detail,
      life: 3000,
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(endpoint);

      setRows(response.data.data);
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Não foi possível carregar os mapeamentos.";

      showError(message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchSelectOptions = useCallback(async () => {
    try {
      const [fiosSpiOptions, fiosPortalOptions] = await Promise.all([
        fetchFiosSpi(),
        fetchFiosPortal(),
      ]);

      setFiosSpi(fiosSpiOptions);
      setFiosPortal(fiosPortalOptions);
    } catch (error) {
      showError("Não foi possível carregar as opções dos selects.");
    }
  }, [showError]);

  function openCreateDialog(fioSpiId = null) {
    setFormData({
      ...initialFormData,
      fio_spi_id: fioSpiId,
    });
    setDialogVisible(true);
  }

  function closeDialog() {
    if (saving) {
      return;
    }

    setDialogVisible(false);
  }

  function updateFormValue(field, value) {
    setFormData((currentFormData) => ({
      ...currentFormData,
      [field]: value,
    }));
  }

  function validateForm() {
    if (!formData.produto_id) {
      showError("Informe o produto.");
      return false;
    }

    if (!formData.fio_spi_id) {
      showError("Informe o fio SPI.");
      return false;
    }

    return true;
  }

  async function saveMapping() {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const response = await api.post(endpoint, formData);
      const message =
        response.data && response.data.message
          ? response.data.message
          : "Mapeamento cadastrado com sucesso.";

      showSuccess(message);
      setDialogVisible(false);
      fetchData();
      fetchSelectOptions();
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Não foi possível cadastrar o mapeamento.";

      showError(message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row) {
    setLoading(true);

    try {
      const response = await api.patch(`${endpoint}/${row.id}/ativo`);
      const message =
        response.data && response.data.message
          ? response.data.message
          : "Status atualizado com sucesso.";

      showSuccess(message);
      fetchData();
      fetchSelectOptions();
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Não foi possível atualizar o status.";

      showError(message);
      setLoading(false);
    }
  }

  function renderActions(row) {
    const label = row.ativo ? "Inativar" : "Ativar";
    const icon = row.ativo ? "pi pi-ban" : "pi pi-check";
    const className = row.ativo ? "p-button-warning" : "p-button-success";

    return (
      <Button
        label={label}
        icon={icon}
        className={`p-button-sm ${className}`}
        onClick={() => toggleActive(row)}
        disabled={loading}
      />
    );
  }

  function renderPendingActions(row) {
    return (
      <Button
        label="Associar"
        icon="pi pi-link"
        className="p-button-sm"
        onClick={() => openCreateDialog(row.idtecfio)}
        disabled={loading}
      />
    );
  }

  function refreshData() {
    fetchData();
    fetchSelectOptions();
  }

  function renderToolbar() {
    return (
      <div className="p-d-flex p-jc-between p-ai-center p-mb-2">
        <Button
          label="Novo mapeamento"
          icon="pi pi-plus"
          onClick={() => openCreateDialog()}
          disabled={loading}
        />
        <Button
          label="Atualizar"
          icon="pi pi-refresh"
          className="p-button-secondary"
          onClick={refreshData}
          disabled={loading}
        />
      </div>
    );
  }

  function renderDialogFooter() {
    return (
      <div>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          className="p-button-text"
          onClick={closeDialog}
          disabled={saving}
        />
        <Button
          label="Salvar"
          icon="pi pi-check"
          onClick={saveMapping}
          loading={saving}
        />
      </div>
    );
  }

  useEffect(() => {
    fetchData();
    fetchSelectOptions();
  }, [fetchData, fetchSelectOptions]);

  return (
    <div>
      <Toast ref={toast} />
      <Menu />

      <Card title="Mapeamento de fios SPI">
        {loading && <Loading />}
        {renderToolbar()}

        <h4 className="p-mt-0 p-mb-2">Fios pendentes de associação</h4>
        <div style={pendingInfoStyle}>
          Os fios abaixo aparecem porque foram usados em artigos com rolo de
          urdume nos últimos seis meses e ainda não possuem mapeamento
          cadastrado.
        </div>
        <DataTable
          className="p-datatable-sm p-mb-3"
          value={fiosSpi}
          emptyMessage="Nenhum fio pendente de associação."
          responsiveLayout="scroll"
          paginator
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
          currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords} fios pendentes"
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
        >
          <Column
            field="idtecfio"
            header="ID Fio SPI"
            headerStyle={pendingHeaderStyle}
            bodyStyle={pendingBodyStyle}
            sortable
          />
          <Column
            field="descricao"
            header="Fio SPI"
            headerStyle={pendingHeaderStyle}
            bodyStyle={pendingBodyStyle}
            sortable
          />
          <Column
            header="Ações"
            headerStyle={pendingHeaderStyle}
            bodyStyle={pendingBodyStyle}
            body={renderPendingActions}
          />
        </DataTable>

        <h4 className="p-mb-2">Mapeamentos cadastrados</h4>
        <DataTable
          className="p-datatable-sm"
          value={rows}
          emptyMessage="Nenhum mapeamento encontrado."
          responsiveLayout="scroll"
          paginator
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
          currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords} mapeamentos"
          rows={20}
          rowsPerPageOptions={[20, 50, 100]}
        >
          <Column
            field="id"
            header="ID"
            headerStyle={compactHeaderStyle}
            bodyStyle={compactBodyStyle}
            sortable
          />
          <Column
            field="produto.descricao"
            header="Produto"
            headerStyle={compactHeaderStyle}
            bodyStyle={compactBodyStyle}
            body={formatProduto}
            sortable
          />
          <Column
            field="fio_spi.descricao"
            header="Fio SPI"
            headerStyle={compactHeaderStyle}
            bodyStyle={compactBodyStyle}
            body={formatFioSpi}
            sortable
          />
          <Column
            field="ativo"
            header="Status"
            headerStyle={compactHeaderStyle}
            bodyStyle={activeBodyStyle}
            body={formatStatus}
            sortable
          />
          <Column
            header="Ações"
            headerStyle={compactHeaderStyle}
            bodyStyle={compactBodyStyle}
            body={renderActions}
          />
        </DataTable>
      </Card>

      <Dialog
        header="Novo mapeamento"
        visible={dialogVisible}
        style={{ width: 420 }}
        modal
        footer={renderDialogFooter()}
        onHide={closeDialog}
      >
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="produto_id">Produto</label>
            <Dropdown
              id="produto_id"
              value={formData.produto_id}
              options={fiosPortal}
              optionLabel="descricao"
              optionValue="id"
              onChange={(event) =>
                updateFormValue("produto_id", event.value)
              }
              placeholder="Selecione o produto"
              filter
              filterPlaceholder="Buscar produto"
              emptyMessage="Nenhum produto disponível."
              emptyFilterMessage="Nenhum produto encontrado."
              disabled={saving}
            />
          </div>
          <div className="p-field">
            <label htmlFor="fio_spi_id">Fio SPI</label>
            <Dropdown
              id="fio_spi_id"
              value={formData.fio_spi_id}
              options={fiosSpi}
              optionLabel="descricao"
              optionValue="idtecfio"
              onChange={(event) =>
                updateFormValue("fio_spi_id", event.value)
              }
              placeholder="Selecione o fio SPI"
              filter
              filterPlaceholder="Buscar fio SPI"
              emptyMessage="Nenhum fio SPI disponível."
              emptyFilterMessage="Nenhum fio SPI encontrado."
              disabled={saving}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
